import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pill, RefreshCw, CheckCircle2, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";

export default function PharmacyDispense() {
  const { branch } = useAuth();
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue/pharmacy", { params: { branch_code: branch.code } });
      setQueue(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load pharmacy queue"));
    } finally {
      setLoading(false);
    }
  }, [branch?.code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="pharmacy-dispense-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Dispensary · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Dispense medicines</h1>
          <p className="text-[#5C5652]">
            Hand over the prescribed items and confirm — the patient&apos;s visit will be marked complete.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="pharmacy-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </button>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
        ) : queue.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-display text-forest text-xl mb-1">No one is waiting for medicines</div>
            <p className="text-sm text-[#5C5652]">Once billing generates a bill, patients arrive here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E3D9]" data-testid="pharmacy-queue-list">
            {queue.map((p, i) => (
              <li key={p.id} className="flex items-center gap-4 px-6 py-4 hover:bg-[#F9F6F0]">
                <div className="h-10 w-10 rounded-full bg-[#1A4331]/10 text-forest flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2A2624]">{p.name}</div>
                  <div className="text-xs text-[#5C5652]">
                    Token {p.op_token}
                    {p.bill?.pharm_token && (
                      <>
                        {" "}· Pharmacy <span className="font-mono text-forest">{p.bill.pharm_token}</span>
                      </>
                    )}
                    {p.bill?.total !== undefined && <> · ₹ {Number(p.bill.total).toFixed(2)}</>}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(p)}
                  data-testid={`open-dispense-${p.mrn}`}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover"
                >
                  <Pill className="h-4 w-4" strokeWidth={1.75} />
                  Dispense
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <DispenseModal
          patient={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function DispenseModal({ patient, onClose, onDone }) {
  const rx = patient?.consultation?.prescription || {};
  const rxItems = rx.items || [];
  const sketch = rx.sketch_data_url;
  const [checked, setChecked] = useState(() => rxItems.map(() => true));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);

  const toggle = (i) => setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const submit = async () => {
    setBusy(true);
    try {
      const dispensedItems = rxItems.filter((_, i) => checked[i]).map((it) => it.drug);
      await api.post(`/pharmacy/${patient.id}/dispense`, {
        dispensed_items: dispensedItems,
        notes,
      });
      toast.success("Dispensed — visit complete");
      setComplete(true);
    } catch (err) {
      toast.error(formatApiError(err, "Dispense failed"));
    } finally {
      setBusy(false);
    }
  };

  if (complete) {
    return (
      <Portal>
      <div
        className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-center justify-center p-6"
        onClick={onDone}
        data-testid="dispense-done-modal"
      >
        <div className="bg-white w-full max-w-md rounded-lg border border-sand p-8 text-center animate-fade-up">
          <div className="mx-auto h-14 w-14 rounded-full bg-[#1A4331]/10 text-forest flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="font-display text-2xl text-forest mb-1">Handed over</div>
          <div className="text-sm text-[#5C5652] mb-6">{patient.name}&apos;s visit is now complete.</div>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              className="flex-1 h-11 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0] inline-flex items-center justify-center gap-2"
            >
              <Printer className="h-4 w-4" strokeWidth={1.75} /> Intake chart
            </button>
            <button
              onClick={onDone}
              data-testid="dispense-done"
              className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover"
            >
              Next patient
            </button>
          </div>
        </div>
      </div>
      </Portal>
    );
  }

  return (
    <Portal>
    <div
      className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={() => !busy && onClose()}
      data-testid="dispense-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-lg border border-sand animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-sand px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1">
            {patient.mrn} · Token {patient.op_token}{" "}
            {patient.bill?.pharm_token && (
              <>
                · Pharmacy <span className="font-mono text-forest">{patient.bill.pharm_token}</span>
              </>
            )}
          </div>
          <div className="font-display text-2xl text-forest">Prescription — {patient.name}</div>
          <div className="text-sm text-[#5C5652]">
            {patient.assigned_doctor_name && <>Prescribed by {patient.assigned_doctor_name}</>}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {patient.consultation?.diagnosis_text && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">
                Diagnosis note
              </div>
              <div className="text-sm text-[#2A2624] whitespace-pre-wrap bg-[#F9F6F0] border border-sand rounded p-3">
                {patient.consultation.diagnosis_text}
              </div>
            </div>
          )}

          {rxItems.length > 0 ? (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">
                Items to dispense
              </div>
              <ul className="divide-y divide-[#E8E3D9] border border-sand rounded-lg" data-testid="dispense-items-list">
                {rxItems.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 p-3">
                    <input
                      type="checkbox"
                      checked={checked[i]}
                      onChange={() => toggle(i)}
                      data-testid={`dispense-check-${i}`}
                      className="mt-1 h-4 w-4 accent-[#1A4331]"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-[#2A2624]">{it.drug}</div>
                      <div className="text-xs text-[#5C5652]">
                        {[it.dose, it.frequency, it.duration].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#F9F6F0] border border-sand text-sm text-[#5C5652]">
              No typed items — check the sketch below (if any) or the doctor&apos;s notes.
            </div>
          )}

          {rx.instructions && (
            <div className="p-3 rounded-lg border border-sand bg-[#F9F6F0]">
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-1">
                Instructions to patient
              </div>
              <div className="text-sm text-[#2A2624] whitespace-pre-wrap">{rx.instructions}</div>
            </div>
          )}

          {sketch && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">
                Handwritten sketch
              </div>
              <img
                src={sketch}
                alt="Prescription sketch"
                data-testid="dispense-sketch"
                className="w-full rounded-lg border border-sand bg-white"
              />
            </div>
          )}

          <textarea
            data-testid="dispense-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Dispense notes (out-of-stock, substituted, etc.)"
            className="w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none"
          />

          <div className="flex gap-3 pt-2 border-t border-sand">
            <button
              onClick={submit}
              disabled={busy}
              data-testid="dispense-submit"
              className="flex-1 h-12 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
              {busy ? "Submitting…" : "Confirm dispense"}
            </button>
            <button
              onClick={onClose}
              disabled={busy}
              className="h-12 px-5 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}
